import logo from '@/assets/logo.png';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { buildOrgUrl } from '@/core/domain';
import { execute } from '@/graphql/execute';
import { cn } from '@/lib/utils';
import { createOrganizationMutation, slugAvailableQuery } from '@/queries/organization.query';
import { useFormik } from 'formik';
import { Loader2 } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const RANDOM_WORDS = [
  'alpha', 'beta', 'delta', 'echo', 'fox', 'nova', 'peak', 'sky',
  'swift', 'wave', 'zen', 'arc', 'bolt', 'core', 'edge', 'flow',
];

const slugify = (text: string) =>
  text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

export const CreateOrganizationForm = ({
  className,
  onSuccess,
  compact,
  ...props
}: React.ComponentProps<'div'> & { onSuccess?: (organizationId: string, slug: string) => void; compact?: boolean }) => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [slugStatus, setSlugStatus] = useState<'idle' | 'checking' | 'available' | 'taken'>('idle');
  const [alternatives, setAlternatives] = useState<string[]>([]);
  const [slug, setSlug] = useState('');

  const checkSlugAvailability = useCallback(async (slugToCheck: string) => {
    if (slugToCheck.length < 3) {
      setSlugStatus('idle');
      return;
    }

    setSlugStatus('checking');
    try {
      const data = await execute(slugAvailableQuery, { slug: slugToCheck });
      if (data.slugAvailable) {
        setSlugStatus('available');
        setAlternatives([]);
      } else {
        setSlugStatus('taken');
        const alts: string[] = [];
        const shuffled = [...RANDOM_WORDS].sort(() => Math.random() - 0.5);
        for (let i = 0; i < 3 && i < shuffled.length; i++) {
          alts.push(`${slugToCheck}-${shuffled[i]}`);
        }
        setAlternatives(alts);
      }
    } catch {
      setSlugStatus('idle');
    }
  }, []);

  useEffect(() => {
    if (!slug || slug.length < 3) {
      setSlugStatus('idle');
      return;
    }

    const timer = setTimeout(() => {
      checkSlugAvailability(slug);
    }, 500);

    return () => clearTimeout(timer);
  }, [slug, checkSlugAvailability]);

  const formik = useFormik({
    initialValues: {
      name: '',
    },
    validate: (values) => {
      const errors: Record<string, string> = {};
      if (!values.name.trim()) {
        errors.name = 'Organization name is required';
      }
      return errors;
    },
    onSubmit: async (values) => {
      if (slugStatus !== 'available') {
        toast.error('Please choose an available URL');
        return;
      }

      setIsLoading(true);
      const toastId = toast.loading('Creating organization...', {
        description: 'Setting up your workspace',
      });

      try {
        const data = await execute(createOrganizationMutation, {
          input: {
            name: values.name.trim(),
            slug,
          },
        });

        toast.success('Organization created!', {
          description: 'Your workspace is ready',
          id: toastId,
        });

        if (onSuccess) {
          onSuccess(data.createOrganization.id, slug);
        } else {
          window.location.href = buildOrgUrl(slug, `/auth/onboarding/invite?organizationId=${data.createOrganization.id}`);
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to create organization';
        toast.error('Failed to create organization', {
          description: errorMessage,
          id: toastId,
        });
      } finally {
        setIsLoading(false);
      }
    },
  });

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    formik.handleChange(e);
    const newSlug = slugify(e.target.value);
    setSlug(newSlug);
  };

  const selectAlternative = (alt: string) => {
    setSlug(alt);
    checkSlugAvailability(alt);
  };

  const formContent = (
    <form onSubmit={formik.handleSubmit}>
      <div className='flex flex-col gap-6'>
        {!compact && (
          <div className='flex flex-col items-center text-center'>
            <img src={logo} alt='Logo' className='size-16 mb-6' />
            <h1 className='text-2xl font-bold'>Create your organization</h1>
            <p className='text-muted-foreground text-balance'>Set up your workspace to get started</p>
          </div>
        )}

        <div className='grid gap-2'>
          <Label htmlFor='name'>Organization Name</Label>
          <Input
            id='name'
            name='name'
            type='text'
            placeholder='My Organization'
            value={formik.values.name}
            onChange={handleNameChange}
            onBlur={formik.handleBlur}
            disabled={isLoading}
            required
          />
          {formik.touched.name && formik.errors.name && (
            <p className='text-sm text-red-500'>{formik.errors.name}</p>
          )}
        </div>

        {slug && (
          <div className='grid gap-2'>
            <Label>Project URL</Label>
            <div className='flex items-center gap-2 p-3 bg-muted rounded-md'>
              <span className='text-sm font-mono'>
                <span className='font-semibold'>{slug}</span>
                <span className='text-muted-foreground'>.skygis.app</span>
              </span>
              {slugStatus === 'checking' && (
                <Loader2 className='size-4 animate-spin text-muted-foreground ml-auto' />
              )}
              {slugStatus === 'available' && (
                <span className='text-sm text-green-600 ml-auto'>Available</span>
              )}
              {slugStatus === 'taken' && (
                <span className='text-sm text-red-500 ml-auto'>Taken</span>
              )}
            </div>

            {slugStatus === 'taken' && alternatives.length > 0 && (
              <div className='grid gap-1.5'>
                <p className='text-sm text-muted-foreground'>Try one of these instead:</p>
                {alternatives.map((alt) => (
                  <button
                    key={alt}
                    type='button'
                    onClick={() => selectAlternative(alt)}
                    className='flex items-center gap-2 p-2 text-left rounded-md hover:bg-muted transition-colors text-sm font-mono'>
                    <span className='font-semibold'>{alt}</span>
                    <span className='text-muted-foreground'>.skygis.app</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        <Button type='submit' className='w-full' disabled={isLoading || slugStatus !== 'available'}>
          {isLoading ? 'Creating...' : 'Create Organization'}
        </Button>
      </div>
    </form>
  );

  if (compact) {
    return (
      <div className={cn('p-6', className)} {...props}>
        {formContent}
      </div>
    );
  }

  return (
    <div className={cn('flex flex-col gap-6 w-full max-w-lg mx-auto', className)} {...props}>
      <Card className='overflow-hidden p-0'>
        <CardContent className='p-6 md:p-8 bg-white dark:bg-stone-900'>{formContent}</CardContent>
      </Card>
    </div>
  );
};
